// page-backlog-review-dossier.ts — R4 : ce qui va être transmis.
//
// Cette modale est **obligatoire et sans échappatoire** entre la décision de
// soumettre un périmètre à une IA et le départ effectif. Elle n'est pas une case
// de consentement à cocher au pas de course : elle doit ressembler à un document
// qu'on parcourt. C'est autant l'argument commercial de la fonctionnalité que sa
// garantie juridique.
//
// Trois choix de rédaction, et ils comptent autant que le code :
//
// 1. **Les compteurs sont le volume RÉEL**, pas une estimation. Ils sont
//    calculés par le serveur sur le dossier déjà construit — un « environ »
//    ruinerait la confiance que cet écran est censé établir.
// 2. **Le ⚠ est en `--warn`, jamais en `--danger`.** C'est une précision, pas
//    une alerte : les noms extérieurs écrits en toutes lettres dans une note ne
//    figurent dans aucune liste connue du tenant, donc ils ne sont pas
//    substitués. On le dit, et on donne à lire le contenu réel juste en dessous.
// 3. **Aucune emphase commerciale.** Trois faits, une limite, et le dossier.
//
// Deux sorties, et leur place dans la modale n'est pas décorative : le PIED
// porte la décision (« Envoyer — 10 crédits », qui EST la confirmation, sans
// seconde modale par-dessus), le CORPS porte l'alternative (« Télécharger le
// kit », qui n'appelle aucun fournisseur et ne coûte aucun crédit).

interface DossierVolumes {
  actions: number;
  notes: number;
  people: number;
  characters: number;
}

interface DossierBody {
  dossier: Record<string, unknown>;
  volumes: DossierVolumes;
}

// Au-delà, le navigateur peine à peindre le bloc et l'utilisateur ne le lira pas
// jusqu'au bout de toute façon. On tronque l'AFFICHAGE, jamais le dossier — et
// on le dit, parce qu'un aperçu silencieusement coupé serait un mensonge sur ce
// qui part.
const PREVIEW_CHARS = 60000;

function _counters(v: DossierVolumes): string {
  const parts = [
    t('review.dossier.countActions', { n: String(v.actions) }),
    t('review.dossier.countNotes', { n: String(v.notes) }),
    t('review.dossier.countChars', { n: v.characters.toLocaleString('fr-FR') }),
  ];
  return `<div style="font-size:var(--fs-lg);font-weight:600;margin-bottom:12px">
      ${parts.map((p) => esc(p)).join(' &nbsp;·&nbsp; ')}
    </div>`;
}

function _guarantees(v: DossierVolumes): string {
  const line = (mark: string, color: string, text: string): string =>
    `<div style="display:flex;gap:8px;margin:6px 0;font-size:var(--fs-sm)">
        <span style="color:${color};font-weight:700">${mark}</span>
        <span>${esc(text)}</span>
      </div>`;
  // « les 0 personnes du périmètre sont remplacées » se lit mal et sonne faux.
  // Un périmètre sans RACI n'a rien à substituer : autant le dire.
  const people = v.people
    ? t('review.dossier.okPeople', { n: String(v.people) })
    : t('review.dossier.okNoPeople');
  return `<div style="margin-bottom:12px">
      ${line('✓', 'var(--accent)', people)}
      ${line('✓', 'var(--accent)', t('review.dossier.okIds'))}
      ${line('⚠', 'var(--warn)', t('review.dossier.warnExternal'))}
    </div>`;
}

function _preview(dossier: Record<string, unknown>): string {
  const full = JSON.stringify(dossier, null, 2);
  const shown = full.length > PREVIEW_CHARS ? full.slice(0, PREVIEW_CHARS) : full;
  const notice =
    full.length > PREVIEW_CHARS
      ? `<div class="text-muted text-xs" style="margin-top:6px">${esc(
          t('review.dossier.previewTruncated', {
            shown: String(PREVIEW_CHARS),
            total: String(full.length),
          }),
        )}</div>`
      : '';
  return `<pre class="text-mono" style="font-size:var(--fs-xs);max-height:40vh;overflow:auto;
      background:var(--bg2);padding:10px;border-radius:4px;white-space:pre-wrap;
      word-break:break-word">${esc(shown)}</pre>${notice}`;
}

/** Ouvre R4. `onStarted` est appelé quand une analyse IA a démarré, pour que
 *  l'écran de revue reprenne son suivi — cette modale ne rend jamais la liste
 *  elle-même. */
export function openDossierModal(reviewId: number, onStarted?: () => void): void {
  _reviewId = reviewId;
  openWideModal(
    t('review.dossier.title'),
    `<div class="loading">${esc(t('common.loading'))}</div>`,
    // Une confirmation s'intercale (demande Christophe, 2026-08-26) : le
    // dossier part hors d'Ornexia et coûte des crédits, deux gestes qu'on ne
    // défait pas. Elle REMPLACE cette modale — `openModal` ferme la
    // précédente — et son annulation la rouvre, pour ne pas faire perdre la
    // lecture du dossier à qui hésite.
    () => _confirmAnalyze(reviewId, onStarted),
    t('review.dossier.send'),
  );
  void _load(reviewId);
}

/** Le dernier arrêt avant l'envoi : ce qui part, ce que ça coûte. */
function _confirmAnalyze(reviewId: number, onStarted?: () => void): void {
  openModal(
    t('review.dossier.confirmTitle'),
    `<p>${esc(t('review.dossier.confirmBody', { n: String(CREDITS) }))}</p>
     <p class="text-muted text-xs">${esc(t('review.dossier.confirmHint'))}</p>`,
    () => _analyze(reviewId, onStarted),
    t('review.dossier.send'),
  );
  // Annuler et la croix rouvrent le dossier : on n'a pas voulu envoyer, on n'a
  // pas voulu tout perdre.
  const reopen = (): void => openDossierModal(reviewId, onStarted);
  document.getElementById('modal-cancel')?.addEventListener('click', reopen);
  document.getElementById('modal-close')?.addEventListener('click', reopen);
}

// Doit rester égal à `ai_pass.CREDITS` côté serveur. Le libellé du bouton porte
// le coût : l'annoncer faux serait pire que ne pas l'annoncer.
const CREDITS = 10;

/** Le corps de la modale, déjà composé et échappé.
 *
 *  Une FONCTION plutôt qu'une variable intermédiaire : `innerHTML = <variable>`
 *  est interdit par ESLint, quel que soit le nom de la variable — la règle ne
 *  sait pas lire une convention de nommage, elle lit une forme syntaxique. Le
 *  suffixe `Html` reste ce qui rend la sûreté auditable à la lecture.
 */
function _bodyHtml(data: DossierBody): string {
  return (
    _counters(data.volumes) +
    _guarantees(data.volumes) +
    _preview(data.dossier) +
    // La sortie latérale : télécharger et soumettre à l'IA de son choix, sans
    // consommer un crédit. Un bouton dans le CORPS, pas dans le pied — le pied
    // porte la décision, celui-ci porte une alternative.
    `<div style="margin-top:12px;display:flex;gap:10px;align-items:center;flex-wrap:wrap">
        <button class="btn btn-secondary" id="btn-download-kit">${esc(t('review.dossier.download'))}</button>
        <span class="text-muted text-xs">${esc(t('review.dossier.kitHint'))}</span>
      </div>`
  );
}

async function _load(reviewId: number): Promise<void> {
  const body = document.getElementById('modal-body');
  if (!body) return;
  try {
    const data = (await api.getBacklogDossier(reviewId)) as DossierBody;
    body.innerHTML = _bodyHtml(data);
    document
      .getElementById('btn-download-kit')
      ?.addEventListener('click', () => void _downloadKit());
  } catch (e: unknown) {
    body.replaceChildren();
    const err = document.createElement('div');
    err.className = 'error';
    err.textContent = e instanceof Error ? e.message : t('common.error');
    body.appendChild(err);
  }
}

// L'identifiant de la revue en cours d'affichage. Porté en module plutôt que
// re-passé partout : la modale n'en montre jamais deux à la fois.
let _reviewId = 0;

async function _downloadKit(): Promise<void> {
  const btn = document.getElementById('btn-download-kit') as HTMLButtonElement | null;
  // Celui-ci VIT DANS LE CORPS de la modale, pas dans son pied : personne ne le
  // désactive à notre place, donc `withButton` a bien un rôle ici.
  await api.withButton(btn, async () => {
    try {
      const out = await api.downloadBacklogKit(_reviewId, `kit-optimisation-${_reviewId}.zip`);
      toast(t('review.dossier.downloaded', { name: out.filename }));
    } catch (e: unknown) {
      // La modale reste ouverte : l'utilisateur doit pouvoir réessayer sans
      // reconstruire le dossier.
      toast(e instanceof Error ? e.message : t('common.error'), 'error');
    }
  });
}

async function _analyze(reviewId: number, onStarted?: () => void): Promise<void> {
  try {
    await api.analyzeBacklogReview(reviewId);
    closeModal();
    // Le calcul vit côté serveur : la modale se ferme, l'écran passe en attente,
    // et l'utilisateur peut quitter la page.
    toast(t('review.dossier.started'));
    onStarted?.();
  } catch (e: unknown) {
    // Consentement absent, crédits insuffisants, analyse déjà en cours : le
    // serveur porte le message, on ne le réécrit pas. La modale reste ouverte —
    // fermer sur un refus ferait perdre le dossier qu'on vient de lire.
    toast(e instanceof Error ? e.message : t('common.error'), 'error');
  }
}
